import { HelpCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MarkdownPreview } from "./MarkdownPreview";

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  placeholder?: string;
  showSaveButton?: boolean;
};

const markdownExample = `### Heading
**Bold text**
*Italic text*
- List item 1
- List item 2

[Link text](https://example.com)`;

export function MarkdownEditor({
  value,
  onChange,
  onSave,
  placeholder = "Enter your markdown content here...",
  showSaveButton = true,
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [helpOpen, setHelpOpen] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (value.trim() !== "") {
        setActiveTab("preview");
      }
    }
  }, [value]);

  return (
    <div className="flex min-h-[480px] flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-1">
          <button
            onClick={() => setActiveTab("edit")}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${
              activeTab === "edit"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Edit
          </button>
          <button
            onClick={() => setActiveTab("preview")}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${
              activeTab === "preview"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Preview
          </button>
        </div>

        <div className="relative flex items-center gap-2">
          <button
            onClick={() => setHelpOpen((current) => !current)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            title="Markdown Help"
          >
            <HelpCircle className="h-4 w-4" />
          </button>

          {showSaveButton && onSave ? (
            <button
              onClick={onSave}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Save
            </button>
          ) : null}

          {helpOpen ? (
            <div className="absolute right-0 top-10 z-10 max-w-sm rounded-md border border-slate-200 bg-white p-3 shadow-lg">
              <p className="mb-1 text-xs font-semibold text-slate-800">
                Markdown Syntax:
              </p>
              <pre className="overflow-auto rounded-sm bg-slate-100 p-2 text-xs text-slate-700">
                {markdownExample}
              </pre>
            </div>
          ) : null}
        </div>
      </div>

      {activeTab === "edit" ? (
        <div className="flex-1">
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            className="thin-scrollbar min-h-[420px] w-full resize-none rounded-md border border-slate-200 p-4 font-mono text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
          />
          <div className="mt-1.5 text-xs text-slate-500">
            Supports Markdown: Use `###` for headings, `**bold**`, `*italic*`, etc.
          </div>
        </div>
      ) : (
        <div className="flex-1">
          <MarkdownPreview content={value} />
        </div>
      )}
    </div>
  );
}
